/**
 * Animation Module - Well diagram (TMAT)
 * Loads SVG template and updates dynamic elements from telemetry data.
 *
 * @requires D3.js
 * @requires moment.js
 * @requires DashboardConfig
 * @requires DashboardAPI
 */

const AnimationModule = (() => {
  const SVG_URL = "/images/well-diagram.svg?v=3";
  const LAYOUT = {
    wellTop: 90,
    wellHeight: 280,
    wellBottom: 370,
    pipeTop: 55,
    waterDefaultY: 200,
    pumpDefaultY: 259,
    sensorDefaultY: 292,
    wellLeftX: 118,
    wellRightX: 282,
    pumpCenterX: 200,
    sensorCenterX: 200,
  };

  const PROXIMITY_PX = 50;
  const CARD_GAP = 8;
  const STACK_OFFSET = 18;
  const KNEE_INSET = 8;

  const CALLOUTS = {
    dat: {
      cardEdgeX: 104,
      defaultCenterY: 131,
      cardHeight: 46,
      groupId: "label-dat-group",
      anchorX: LAYOUT.wellLeftX,
      side: "left",
    },
    pump: {
      cardEdgeX: 80,
      defaultCenterY: 334,
      cardHeight: 32,
      groupId: "label-pump-group",
      anchorX: LAYOUT.pumpCenterX,
      side: "left",
    },
    mat: {
      cardEdgeX: 296,
      defaultCenterY: 134,
      cardHeight: 52,
      groupId: "label-mat-group",
      anchorX: LAYOUT.wellRightX,
      side: "right",
    },
    sensor: {
      cardEdgeX: 320,
      defaultCenterY: 334,
      cardHeight: 32,
      groupId: "label-sensor-group",
      anchorX: LAYOUT.sensorCenterX,
      side: "right",
    },
  };

  let _wellConfig = {
    wellDepth: null,
    pumpDepth: null,
    sensorDepth: null,
    wellElevation: null,
  };
  let _reading = null;
  let _deviceId = null;
  let _pollInterval = null;
  let _templateLoaded = false;

  const _fmt = (value) => {
    if (typeof DashboardHelpers !== "undefined" && DashboardHelpers.formatNumber) {
      return DashboardHelpers.formatNumber(value);
    }
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  };

  /** Kedalaman dari muka sumur (cm) → koordinat Y SVG */
  const _depthToY = (depthCm) =>
    LAYOUT.wellTop + (depthCm / _wellConfig.wellDepth) * LAYOUT.wellHeight;

  const _clampCardCenterY = (centerY, cardHeight) => {
    const min = LAYOUT.wellTop - 10 + cardHeight / 2;
    const max = LAYOUT.wellBottom + 30 - cardHeight / 2;
    return Math.max(min, Math.min(max, centerY));
  };

  /** When anchors are close, stack cards above/below; otherwise align card with anchor */
  const _layoutPair = (anchorA, anchorB, heightA, heightB) => {
    let centerA;
    let centerB;

    if (Math.abs(anchorA - anchorB) < PROXIMITY_PX) {
      const mid = (anchorA + anchorB) / 2;
      centerA = mid - STACK_OFFSET - heightA / 2;
      centerB = mid + STACK_OFFSET + heightB / 2;
    } else {
      centerA = anchorA;
      centerB = anchorB;
    }

    const minDist = heightA / 2 + heightB / 2 + CARD_GAP;
    if (Math.abs(centerA - centerB) < minDist) {
      const shift = (minDist - Math.abs(centerA - centerB)) / 2 + 1;
      if (centerA <= centerB) {
        centerA -= shift;
        centerB += shift;
      } else {
        centerA += shift;
        centerB -= shift;
      }
    }

    return {
      a: _clampCardCenterY(centerA, heightA),
      b: _clampCardCenterY(centerB, heightB),
    };
  };

  const _layoutCardCenters = (surfaceY, pumpY, sensorY) => {
    const left = _layoutPair(
      surfaceY,
      pumpY,
      CALLOUTS.dat.cardHeight,
      CALLOUTS.pump.cardHeight,
    );
    const right = _layoutPair(
      surfaceY,
      sensorY,
      CALLOUTS.mat.cardHeight,
      CALLOUTS.sensor.cardHeight,
    );

    return {
      dat: left.a,
      pump: left.b,
      mat: right.a,
      sensor: right.b,
    };
  };

  const _isConfigReady = () =>
    _wellConfig.wellDepth > 0 &&
    _wellConfig.pumpDepth > 0 &&
    _wellConfig.sensorDepth > 0;

  const _showPanel = () => {
    const panel = document.getElementById("well-diagram-panel");
    if (panel) panel.style.display = "block";
  };

  const _hidePanel = () => {
    const panel = document.getElementById("well-diagram-panel");
    if (panel) panel.style.display = "none";

    const content = document.getElementById("well-diagram-content");
    const empty = document.getElementById("well-diagram-empty");
    const diagram = document.getElementById("diagram");

    if (content) content.style.display = "none";
    if (empty) empty.style.display = "none";
    if (diagram) diagram.style.display = "none";

    _hideShimmer();
  };

  const _showDiagramContent = () => {
    _showPanel();

    const content = document.getElementById("well-diagram-content");
    const empty = document.getElementById("well-diagram-empty");

    if (content) content.style.display = "block";
    if (empty) empty.style.display = "none";
  };

  const _showEmptyState = (
    title = "Data Sumur Belum Diatur",
    description = "Ilustrasi sumur tidak dapat ditampilkan karena pengaturan kedalaman sumur, pompa, atau sensor belum lengkap.",
  ) => {
    _showPanel();
    _hideShimmer();

    const content = document.getElementById("well-diagram-content");
    const empty = document.getElementById("well-diagram-empty");
    const titleEl = document.getElementById("well-diagram-empty-title");
    const descEl = document.getElementById("well-diagram-empty-desc");
    const diagram = document.getElementById("diagram");

    if (content) content.style.display = "none";
    if (empty) empty.style.display = "block";
    if (diagram) diagram.style.display = "none";
    if (titleEl) titleEl.textContent = title;
    if (descEl) descEl.textContent = description;
  };

  const _checkAndShowDiagram = () => {
    const diagram = document.getElementById("diagram");
    const ready = _isConfigReady();

    if (ready) {
      _showDiagramContent();
      if (diagram) diagram.style.display = "block";
    } else {
      _showEmptyState();
    }

    return ready;
  };

  const _showShimmer = () => {
    _showDiagramContent();

    const wrapper = document.querySelector(".well-diagram-wrapper");
    const shimmer = document.getElementById("well-diagram-shimmer");
    const lastUpdateShimmer = document.getElementById(
      "well-diagram-last-update-shimmer",
    );
    const lastUpdate = document.getElementById("last-update-fw");
    const diagram = document.getElementById("diagram");

    if (wrapper) wrapper.classList.add("is-loading");
    if (shimmer) {
      shimmer.classList.remove("is-hidden");
      shimmer.setAttribute("aria-busy", "true");
    }
    if (lastUpdateShimmer) lastUpdateShimmer.style.display = "block";
    if (lastUpdate) lastUpdate.classList.add("is-hidden");
    if (diagram) diagram.style.display = "none";
  };

  const _hideShimmer = () => {
    const wrapper = document.querySelector(".well-diagram-wrapper");
    const shimmer = document.getElementById("well-diagram-shimmer");
    const lastUpdateShimmer = document.getElementById(
      "well-diagram-last-update-shimmer",
    );
    const lastUpdate = document.getElementById("last-update-fw");

    if (wrapper) wrapper.classList.remove("is-loading");
    if (shimmer) {
      shimmer.classList.add("is-hidden");
      shimmer.setAttribute("aria-busy", "false");
    }
    if (lastUpdateShimmer) lastUpdateShimmer.style.display = "none";
    if (lastUpdate) lastUpdate.classList.remove("is-hidden");
  };

  const _setText = (id, text, animate = false) => {
    const sel = d3.select(`#diagram #${id}`);
    if (sel.empty()) return;
    if (animate) {
      sel.transition()
        .duration(DashboardConfig.well.transitionDuration)
        .text(text);
    } else {
      sel.text(text);
    }
  };

  const _setTransform = (id, dy, animate = false) => {
    const sel = d3.select(`#diagram #${id}`);
    if (sel.empty()) return;
    const value = `translate(0, ${dy})`;
    if (animate) {
      sel.transition()
        .duration(DashboardConfig.well.transitionDuration)
        .attr("transform", value);
    } else {
      sel.attr("transform", value);
    }
  };

  /** Short connector — straight when aligned, minimal elbow otherwise */
  const _connectorPath = (anchorX, anchorY, cardEdgeX, cardY, side) => {
    if (Math.abs(anchorY - cardY) < 10) {
      return `M ${anchorX} ${anchorY} L ${cardEdgeX} ${cardY}`;
    }

    const kneeX =
      side === "left" ? anchorX - KNEE_INSET : anchorX + KNEE_INSET;

    return `M ${anchorX} ${anchorY} L ${kneeX} ${anchorY} L ${kneeX} ${cardY} L ${cardEdgeX} ${cardY}`;
  };

  const _updateCallout = (key, anchorY, cardCenterY, animate) => {
    const cfg = CALLOUTS[key];
    const path = _connectorPath(
      cfg.anchorX,
      anchorY,
      cfg.cardEdgeX,
      cardCenterY,
      cfg.side,
    );
    const cardDy = cardCenterY - cfg.defaultCenterY;

    _setTransform(cfg.groupId, cardDy, animate);

    const connector = d3.select(`#diagram #${key}-connector`);
    const anchor = d3.select(`#diagram #${key}-anchor`);

    if (!connector.empty()) {
      if (animate) {
        connector
          .transition()
          .duration(DashboardConfig.well.transitionDuration)
          .attr("d", path);
      } else {
        connector.attr("d", path);
      }
    }

    if (!anchor.empty()) {
      if (animate) {
        anchor
          .transition()
          .duration(DashboardConfig.well.transitionDuration)
          .attr("cx", cfg.anchorX)
          .attr("cy", anchorY);
      } else {
        anchor.attr("cx", cfg.anchorX).attr("cy", anchorY);
      }
    }
  };

  const _updateLastReading = (timestamp) => {
    const el = document.getElementById("last-update-fw");
    if (!el || !timestamp) return;

    const dateStr = timestamp.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const timeStr = timestamp
      .toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
      .replace(".", ":");

    el.textContent = `Last Update: ${dateStr}  ${timeStr}`;
  };

  const _applyReading = (animate = false) => {
    if (!_wellConfig.wellDepth || !_reading) return;

    const matCm = _reading.waterLevel * 100;
    const datCm = _wellConfig.wellDepth - matCm;
    const surfaceY = _depthToY(matCm);
    const waterHeight = LAYOUT.wellBottom - surfaceY;
    const pumpY = _depthToY(_wellConfig.pumpDepth);
    const sensorY = _depthToY(_wellConfig.sensorDepth);
    const matM = matCm / 100;
    const datM = datCm / 100;
    const cardCenters = _layoutCardCenters(surfaceY, pumpY, sensorY);

    const waterSel = d3.select("#diagram #water-fill");
    if (!waterSel.empty()) {
      if (animate) {
        waterSel
          .transition()
          .duration(DashboardConfig.well.transitionDuration)
          .attr("y", surfaceY)
          .attr("height", Math.max(0, waterHeight));
      } else {
        waterSel.attr("y", surfaceY).attr("height", Math.max(0, waterHeight));
      }
    }

    const surfaceDy = surfaceY - LAYOUT.waterDefaultY;
    _setTransform("water-surface-group", surfaceDy, animate);

    const pumpDy = pumpY - LAYOUT.pumpDefaultY;
    _setTransform("pump-group", pumpDy, animate);

    const pipeSel = d3.select("#diagram #pump-pipe");
    if (!pipeSel.empty()) {
      const pipeHeight = Math.max(10, pumpY - LAYOUT.pipeTop + 8);
      if (animate) {
        pipeSel
          .transition()
          .duration(DashboardConfig.well.transitionDuration)
          .attr("height", pipeHeight);
      } else {
        pipeSel.attr("height", pipeHeight);
      }
    }

    const sensorDy = sensorY - LAYOUT.sensorDefaultY;
    _setTransform("sensor-group", sensorDy, animate);

    _updateCallout("dat", surfaceY, cardCenters.dat, animate);
    _updateCallout("mat", surfaceY, cardCenters.mat, animate);
    _updateCallout("pump", pumpY, cardCenters.pump, animate);
    _updateCallout("sensor", sensorY, cardCenters.sensor, animate);

    _setText("depth-value", `${_fmt(_wellConfig.wellDepth / 100)} m`, animate);
    _setText("label-mat-value", `${_fmt(matM)} m`, animate);
    _setText("label-dat-value", `${_fmt(datM)} m`, animate);
    _setText(
      "label-pump-value",
      `${_fmt(_wellConfig.pumpDepth / 100)} m`,
      animate,
    );
    _setText(
      "label-sensor-value",
      `${_fmt(_wellConfig.sensorDepth / 100)} m`,
      animate,
    );

    let elvText = "Elv. MAT: -";
    if (_wellConfig.wellElevation != null) {
      const elv = _wellConfig.wellElevation - matM;
      const change = _reading.changeValue || 0;
      const sign = change >= 0 ? "+" : "";
      elvText = `Elv. MAT: ${_fmt(elv)} mdpl (${sign}${_fmt(change)})`;
    }
    _setText("label-elv-value", elvText, animate);
    _updateLastReading(_reading.timestamp);
  };

  const _loadTemplate = async () => {
    const container = document.getElementById("diagram");
    if (!container) return false;

    if (_templateLoaded && container.querySelector("svg")) {
      return true;
    }

    container.innerHTML = "";
    const doc = await d3.xml(SVG_URL);
    container.appendChild(document.importNode(doc.documentElement, true));
    _templateLoaded = true;
    return true;
  };

  const _fetchWellConfiguration = async (deviceId) => {
    try {
      const config = await DashboardAPI.getWellConfig(deviceId);
      const unit = config.wellDepthUnit || "m";
      const multiplier = unit === "m" ? 100 : 1;

      _wellConfig = {
        wellDepth: config.wellDepth ? config.wellDepth * multiplier : null,
        pumpDepth: config.pumpDepth ? config.pumpDepth * multiplier : null,
        sensorDepth: config.sensorDepth ? config.sensorDepth * multiplier : null,
        wellElevation: config.wellElevation ?? null,
      };

      return _isConfigReady();
    } catch (error) {
      _wellConfig = {
        wellDepth: null,
        pumpDepth: null,
        sensorDepth: null,
        wellElevation: null,
      };
      return false;
    }
  };

  const _fetchWaterLevelData = async (deviceId) => {
    try {
      const data = await DashboardAPI.getWellData(deviceId);
      if (!Array.isArray(data) || data.length === 0) return;

      const item = data[0];
      const waterLevelInMeters = parseFloat(item.waterLevel);

      _reading = {
        timestamp: new Date(moment.parseZone(item.readingAtUtc)),
        waterLevel: waterLevelInMeters,
        changeValue: parseFloat(item.changeValue) || 0,
      };
    } catch (error) {
      // keep previous reading if refresh fails
    }
  };

  const _stopPolling = () => {
    if (_pollInterval) {
      clearInterval(_pollInterval);
      _pollInterval = null;
    }
  };

  const _startPolling = () => {
    _stopPolling();
    _pollInterval = setInterval(async () => {
      if (!_deviceId) return;
      await _fetchWaterLevelData(_deviceId);
      if (_reading) _applyReading(true);
    }, DashboardConfig.intervals.dataRefresh);
  };

  const _render = async (animate = false) => {
    const loaded = await _loadTemplate();
    if (!loaded || !_checkAndShowDiagram()) return;
    _applyReading(animate);
  };

  return {
    init() {},

    async loadWellData(deviceId) {
      _deviceId = deviceId;
      _reading = null;
      _templateLoaded = false;
      _stopPolling();
      _showShimmer();

      try {
        const configOk = await _fetchWellConfiguration(deviceId);

        if (!configOk) {
          _showEmptyState();
          return;
        }

        await _fetchWaterLevelData(deviceId);
        await _render(false);
        _hideShimmer();
        _checkAndShowDiagram();
        _startPolling();
      } catch (error) {
        _showEmptyState(
          "Gagal Memuat Ilustrasi",
          "Terjadi kesalahan saat memuat data ilustrasi sumur. Silakan coba lagi.",
        );
      }
    },

    reset() {
      _deviceId = null;
      _reading = null;
      _templateLoaded = false;
      _wellConfig = {
        wellDepth: null,
        pumpDepth: null,
        sensorDepth: null,
        wellElevation: null,
      };
      _stopPolling();
      _hidePanel();
    },

    handleResize() {
      if (_wellConfig.wellDepth && _reading) {
        _applyReading(false);
      }
    },

    stop() {
      _stopPolling();
    },

    hide() {
      this.reset();
    },

    start() {
      _startPolling();
    },
  };
})();

window.AnimationModule = AnimationModule;
